import type { MarketAwarenessDossier } from "../types/market-awareness";

export interface MarketPriceActionLine {
  instrument: string;
  price: string;
  move: string;
  technical: string;
  vwap: string;
  sourceState: string;
  source: string;
  asOf: string;
}

export interface MarketPriceActionGrid {
  instrument: string;
  price: string;
  pointChange: number | null;
  pointText: string;
  percentChange: number | null;
  percentText: string;
  vwapDistance: number | null;
  vwapText: string;
}

export interface MarketPriceInterestLevel {
  id: "putWall" | "hvl" | "callWall";
  label: string;
  sourceLabel: "Adj." | "On-Chain";
  value: number | null;
  valueText: string;
  distance: number | null;
  distanceText: string;
  tone: "bullish" | "bearish" | "primary";
}

export interface MarketPricesOfInterest {
  instrument: string;
  price: string;
  sourceState: string;
  source: string;
  asOf: string;
  levels: MarketPriceInterestLevel[];
}

export function normalizeMarketAwarenessSymbol(symbol: string): string {
  const trimmed = symbol.trim().toUpperCase();
  if (!trimmed) return "/NQ";
  return trimmed.startsWith("/") ? trimmed : `/${trimmed}`;
}

export function formatMarketPriceActionLine(
  dossier: MarketAwarenessDossier | null,
  fallbackSymbol: string,
): MarketPriceActionLine {
  const instrument =
    dossier?.instrument ?? normalizeMarketAwarenessSymbol(fallbackSymbol);
  const frame15 = dossier?.technicals.timeframe15m ?? null;
  const frame1h = dossier?.technicals.timeframe1h ?? null;
  const latest =
    dossier?.priceAction.last ??
    frame15?.latestClose ??
    frame1h?.latestClose ??
    null;
  const pointChange =
    dossier?.priceAction.pointChange ?? dossier?.performance.day.points ?? null;
  const percentChange =
    dossier?.priceAction.percentChange ??
    dossier?.performance.day.percent ??
    null;

  return {
    instrument,
    price: formatPrice(latest),
    move: formatMove(pointChange, percentChange),
    technical: formatTechnical(frame15, frame1h),
    vwap: formatVwap(dossier?.session.distanceFromVwap ?? null),
    sourceState: dossier?.sourceState ?? "pending",
    source: dossier?.priceAction.source ?? "pending",
    asOf: formatAsOf(dossier?.priceAction.timestamp ?? dossier?.generatedAt),
  };
}

export function formatMarketPriceActionGrid(
  dossier: MarketAwarenessDossier | null,
  fallbackSymbol: string,
): MarketPriceActionGrid {
  const instrument =
    dossier?.instrument ?? normalizeMarketAwarenessSymbol(fallbackSymbol);
  const frame15 = dossier?.technicals.timeframe15m ?? null;
  const frame1h = dossier?.technicals.timeframe1h ?? null;
  const latest =
    dossier?.priceAction.last ??
    frame15?.latestClose ??
    frame1h?.latestClose ??
    null;
  const pointChange =
    dossier?.priceAction.pointChange ?? dossier?.performance.day.points ?? null;
  const percentChange =
    dossier?.priceAction.percentChange ??
    dossier?.performance.day.percent ??
    null;
  const vwapDistance = dossier?.session.distanceFromVwap ?? null;

  return {
    instrument,
    price: latest == null ? "pending" : formatPrice(latest),
    pointChange,
    pointText: formatGridPoints(pointChange),
    percentChange,
    percentText: formatGridPercent(percentChange),
    vwapDistance,
    vwapText: formatGridPoints(vwapDistance),
  };
}

export function formatMarketPricesOfInterest(
  dossier: MarketAwarenessDossier | null,
  fallbackSymbol: string,
): MarketPricesOfInterest {
  const instrument =
    dossier?.instrument ?? normalizeMarketAwarenessSymbol(fallbackSymbol);
  const latest = latestMarketPrice(dossier);
  const basisGex = dossier?.basisGex ?? null;
  const sourceState = basisGex?.status ?? dossier?.sourceState ?? "pending";
  const source = basisGex?.source ?? "basis GEX pending";
  const sourceLabel = basisGex?.levelSourceLabel ?? levelSourceLabel(source);
  const levelSpecs: Array<{
    id: MarketPriceInterestLevel["id"];
    label: string;
    value: number | null | undefined;
    tone: MarketPriceInterestLevel["tone"];
  }> = [
    {
      id: "putWall",
      label: "Put Wall",
      value: basisGex?.putWall,
      tone: "bearish",
    },
    { id: "hvl", label: "HVL", value: basisGex?.hvl, tone: "primary" },
    {
      id: "callWall",
      label: "Call Wall",
      value: basisGex?.callWall,
      tone: "bullish",
    },
  ];

  return {
    instrument,
    price: formatPrice(latest),
    sourceState,
    source,
    asOf: formatAsOf(dossier?.priceAction.timestamp ?? dossier?.generatedAt),
    levels: levelSpecs.map((level) => {
      const value =
        level.value == null || !Number.isFinite(Number(level.value))
          ? null
          : Number(level.value);
      const distance =
        value == null || latest == null ? null : Number(value) - Number(latest);
      return {
        id: level.id,
        label: level.label,
        sourceLabel,
        value,
        valueText: value == null ? "pending" : formatPrice(value),
        distance,
        distanceText: formatPoiDistance(distance),
        tone: level.tone,
      };
    }),
  };
}

export function formatPrice(value: number | null): string {
  if (value == null) return "price pending";
  if (Math.abs(value) >= 100) return value.toFixed(2);
  return value.toFixed(3);
}

function latestMarketPrice(dossier: MarketAwarenessDossier | null) {
  return (
    dossier?.priceAction.last ??
    dossier?.technicals.timeframe15m.latestClose ??
    dossier?.technicals.timeframe1h.latestClose ??
    null
  );
}

function levelSourceLabel(
  source: string,
): MarketPriceInterestLevel["sourceLabel"] {
  if (
    /on[- ]?futures|futures[- ]?options|databento|glbx|cme|cbot|nymex|comex|real options/i.test(
      source,
    )
  )
    return "On-Chain";
  return "Adj.";
}

function formatMove(points: number | null, percent: number | null): string {
  if (points == null && percent == null) return "move pending";
  const pointText = points == null ? "n/a" : `${signed(points)} pts`;
  const percentText = percent == null ? "n/a" : `${signed(percent)}%`;
  return `${pointText} / ${percentText}`;
}

function formatTechnical(
  frame15: MarketAwarenessDossier["technicals"]["timeframe15m"] | null,
  frame1h: MarketAwarenessDossier["technicals"]["timeframe1h"] | null,
): string {
  const fifteen =
    frame15 && frame15.bars >= 20
      ? `15m ${frame15.trend}${frame15.pattern !== "none" ? ` ${frame15.pattern}` : ""}`
      : "15m pending";
  const hourly =
    frame1h && frame1h.bars >= 20 ? `1h ${frame1h.trend}` : "1h pending";
  return `${fifteen}; ${hourly}`;
}

function formatVwap(distance: number | null): string {
  if (distance == null) return "VWAP pending";
  return `VWAP ${signed(distance)} pts`;
}

function formatAsOf(value: string | null | undefined): string {
  if (!value) return "as of pending";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "as of pending";
  return `as of ${date.toLocaleTimeString([], {
    hour: "2-digit",
    minute: "2-digit",
    second: "2-digit",
  })}`;
}

function signed(value: number): string {
  const formatted =
    Math.abs(value) >= 100 ? value.toFixed(1) : value.toFixed(2);
  return `${value >= 0 ? "+" : ""}${formatted}`;
}

function formatGridPoints(value: number | null): string {
  if (value == null) return "pending";
  return `${value >= 0 ? "" : "-"}${Math.abs(value).toFixed(2)} pts`;
}

function formatGridPercent(value: number | null): string {
  if (value == null) return "pending";
  return `${value >= 0 ? "+" : "-"}${Math.abs(value).toFixed(2)}%`;
}

function formatPoiDistance(value: number | null): string {
  if (value == null) return "distance pending";
  if (Math.abs(value) < 0.005) return "at tape";
  return `${signed(value)} pts`;
}
